import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useUpdateLateCancellationCallNotification } from "@src/appV2/FacilityProfile/api/useUpdateLateCancellationCallNotification";
import type { TuFunction } from "@src/appV2/i18n/types";
import { useTu } from "@src/appV2/i18n/useTu";
import { formatUsPhoneNumber, useToast } from "@src/appV2/lib";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useState } from "react";

interface UseLateCancellationCallNotificationParams {
  workplace: WorkplaceProfileResponse | undefined;
  onSettingsChanged: () => void;
}

function getDefaultInstructions(
  workplace: WorkplaceProfileResponse | undefined,
  tu: TuFunction
): string {
  const phone = workplace?.data?.attributes?.phone;
  if (isDefined(phone) && phone.length > 0) {
    return tu("Call {{phoneNumber}}", { values: { phoneNumber: formatUsPhoneNumber(phone) } });
  }
  return tu("Call the workplace phone number");
}

export function useLateCancellationCallNotification({
  workplace,
  onSettingsChanged,
}: UseLateCancellationCallNotificationParams) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const existingConfig = workplace?.data?.attributes?.workerAttendanceNotifications;
  const workplaceId = workplace?.data?.id ?? "";

  const [enabled, setEnabled] = useState(existingConfig?.enabled ?? false);

  const instructionsText = existingConfig?.instructions ?? getDefaultInstructions(workplace, tu);

  const editInstructionsModalState = useModalState(ModalStatus.CLOSED);

  const { isLoading: isUpdating, mutateAsync: updateLateCancellationCallNotification } =
    useUpdateLateCancellationCallNotification();

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      editInstructionsModalState.openModal();
      return;
    }

    setEnabled(false);
    try {
      await updateLateCancellationCallNotification({
        workplaceId,
        workerAttendanceNotifications: { enabled: false },
      });
      showSuccessToast(tu("Late-cancellation call notification disabled successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update late-cancellation call notification."));
      setEnabled(true);
    }
  };

  const handleInstructionsSave = async (instructions: string) => {
    try {
      await updateLateCancellationCallNotification({
        workplaceId,
        workerAttendanceNotifications: { enabled: true, instructions },
      });
      setEnabled(true);
      editInstructionsModalState.closeModal();
      showSuccessToast(tu("Late-cancellation call notification updated successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update late-cancellation call notification."));
    }
  };

  return {
    editInstructionsModalState,
    enabled,
    handleInstructionsSave,
    handleToggle,
    instructionsText,
    isUpdating,
  };
}
